import { supabase } from './supabase';

// Types
export interface RealtimeMetrics {
  organization_id: string;
  open_conversations: number;
  pending_conversations: number;
  unassigned_count: number;
  urgent_count: number;
  sla_breached_count: number;
  avg_first_response_24h: number | null;
  ai_resolved_24h: number;
  total_24h: number;
}

export interface Conversation {
  id: string;
  organization_id: string;
  type: string;
  channel: string;
  title: string | null;
  status: string;
  priority: string;
  assigned_agent_id: string | null;
  assigned_team: string | null;
  ai_handled: boolean;
  ai_resolution_confidence: number | null;
  sla_breached: boolean;
  csat_rating: number | null;
  tags: string[];
  created_at: string;
  updated_at: string;
  agent?: {
    id: string;
    display_name: string;
    avatar_url: string | null;
  };
}

export interface Profile {
  id: string;
  organization_id: string | null;
  display_name: string | null;
  email: string | null;
  role: string;
  status: string;
  department: string | null;
  avatar_url: string | null;
  last_seen_at: string | null;
  created_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface DocumentSource {
  id: string;
  organization_id: string;
  title: string;
  description: string | null;
  source_type: string;
  access_level: string;
  processing_status: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConversationMetric {
  id: string;
  organization_id: string;
  period_start: string;
  period_end: string;
  period_type: string;
  total_conversations: number;
  new_conversations: number;
  resolved_conversations: number;
  ai_resolved: number;
  human_resolved: number;
  avg_first_response_seconds: number | null;
  avg_resolution_seconds: number | null;
  avg_csat_score: number | null;
  containment_rate: number | null;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string | null;
  sender_type: string;
  content: string | null;
  content_type: string;
  ai_model: string | null;
  ai_confidence: number | null;
  is_private: boolean;
  created_at: string;
  sender?: {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface CannedResponse {
  id: string;
  organization_id: string;
  short_code: string;
  title: string;
  content: string;
  category: string | null;
  usage_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  creator?: {
    id: string;
    display_name: string | null;
  };
}

export interface AutomationCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'is_empty' | 'is_not_empty';
  value: string;
}

export interface MacroAction {
  type: 'assign_agent' | 'set_priority' | 'set_status' | 'add_label' | 'remove_label' | 'send_message' | 'send_email_notification';
  value: string;
}

export interface AutomationRule {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  trigger_event: string;
  conditions: AutomationCondition[];
  actions: MacroAction[];
  execution_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Macro {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  actions: MacroAction[];
  visibility: string;
  created_by: string | null;
  created_at: string;
}

export interface SlaPolicy {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  first_response_target: Record<string, number>;
  resolution_target: Record<string, number>;
  business_hours: Record<string, unknown>;
  breach_actions: Record<string, boolean>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WebhookSubscription {
  id: string;
  organization_id: string;
  url: string;
  secret: string;
  event_types: string[];
  filters: Record<string, unknown>;
  is_active: boolean;
  last_success_at: string | null;
  last_failure_at: string | null;
  consecutive_failures: number;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuditLogEntry {
  id: string;
  organization_id: string;
  actor_id: string | null;
  actor_type: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string;
  actor?: {
    id: string;
    display_name: string | null;
    email: string | null;
  };
}

export interface AgentActivity {
  id: string;
  organization_id: string;
  agent_id: string;
  activity_type: string;
  conversation_id: string | null;
  details: Record<string, unknown>;
  created_at: string;
  agent?: {
    id: string;
    display_name: string | null;
    email: string | null;
  };
}

export interface ApiKey {
  id: string;
  organization_id: string;
  name: string;
  key_hash: string;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
}

// Sanitize search input to prevent PostgREST filter injection
function sanitizeFilterValue(value: string): string {
  return value.replace(/[^a-zA-Z0-9\s\-_@]/g, '');
}

// Admin API functions
export const adminApi = {
  // Dashboard metrics
  async getDashboardMetrics(organizationId: string): Promise<RealtimeMetrics | null> {
    const { data, error } = await supabase
      .from('realtime_org_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .single();

    if (error) {
      console.error('Error fetching dashboard metrics:', error);
      return null;
    }

    return data;
  },

  // Conversations
  async getConversations(
    organizationId: string,
    options?: { status?: string; search?: string; tag?: string }
  ): Promise<Conversation[]> {
    let query = supabase
      .from('conversations')
      .select(`
        *,
        agent:profiles!assigned_agent_id(id, display_name, avatar_url)
      `)
      .eq('organization_id', organizationId)
      .order('updated_at', { ascending: false });

    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }

    if (options?.tag) {
      query = query.contains('tags', [options.tag]);
    }

    if (options?.search) {
      const safe = sanitizeFilterValue(options.search);
      query = query.or(`title.ilike.%${safe}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }

    return data || [];
  },

  async getConversation(conversationId: string): Promise<Conversation | null> {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        agent:profiles!assigned_agent_id(id, display_name, avatar_url)
      `)
      .eq('id', conversationId)
      .single();

    if (error) {
      console.error('Error fetching conversation:', error);
      return null;
    }

    return data;
  },

  async getConversationMessages(conversationId: string): Promise<Message[]> {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles(id, display_name, avatar_url)
      `)
      .eq('conversation_id', conversationId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
      return [];
    }

    return data || [];
  },

  async updateConversation(id: string, updates: Partial<Conversation>): Promise<boolean> {
    const { error } = await supabase
      .from('conversations')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating conversation:', error);
      return false;
    }

    return true;
  },

  async bulkUpdateConversations(
    ids: string[],
    updates: Partial<Pick<Conversation, 'status' | 'priority' | 'assigned_agent_id' | 'tags'>>
  ): Promise<boolean> {
    const { error } = await supabase
      .from('conversations')
      .update(updates)
      .in('id', ids);

    if (error) {
      console.error('Error bulk updating conversations:', error);
      return false;
    }

    return true;
  },

  // Users
  async getUsers(organizationId: string): Promise<Profile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching users:', error);
      return [];
    }

    return data || [];
  },

  async updateUser(id: string, updates: Partial<Profile>): Promise<boolean> {
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating user:', error);
      return false;
    }

    return true;
  },

  // Organizations
  async getOrganizations(): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching organizations:', error);
      return [];
    }

    return data || [];
  },

  async getOrganization(id: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching organization:', error);
      return null;
    }

    return data;
  },

  async updateOrganization(id: string, updates: Partial<Organization>): Promise<boolean> {
    const { error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating organization:', error);
      return false;
    }

    return true;
  },

  // Documents
  async getDocuments(organizationId: string): Promise<DocumentSource[]> {
    const { data, error } = await supabase
      .from('document_sources')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching documents:', error);
      return [];
    }

    return data || [];
  },

  // Analytics
  async getAnalytics(
    organizationId: string,
    periodType: 'hour' | 'day' | 'week' | 'month' = 'day',
    startDate?: string
  ): Promise<ConversationMetric[]> {
    let query = supabase
      .from('conversation_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('period_type', periodType)
      .order('period_start', { ascending: false })
      .limit(30);

    if (startDate) {
      query = query.gte('period_start', startDate);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching analytics:', error);
      return [];
    }

    return data || [];
  },

  // Recent conversations for dashboard
  async getRecentConversations(organizationId: string, limit = 5): Promise<Conversation[]> {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        agent:profiles!assigned_agent_id(id, display_name, avatar_url)
      `)
      .eq('organization_id', organizationId)
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching recent conversations:', error);
      return [];
    }

    return data || [];
  },

  // Get agents for assignment dropdown
  async getAgents(organizationId: string): Promise<Profile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('organization_id', organizationId)
      .in('role', ['agent', 'supervisor', 'admin', 'owner'])
      .order('display_name');

    if (error) {
      console.error('Error fetching agents:', error);
      return [];
    }

    return data || [];
  },

  // Canned responses
  async getCannedResponses(organizationId: string, search?: string): Promise<CannedResponse[]> {
    let query = supabase
      .from('canned_responses')
      .select('*, creator:profiles!created_by(id, display_name)')
      .eq('organization_id', organizationId)
      .order('usage_count', { ascending: false });

    if (search) {
      const safe = sanitizeFilterValue(search);
      query = query.or(
        `short_code.ilike.%${safe}%,title.ilike.%${safe}%,content.ilike.%${safe}%`
      );
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching canned responses:', error);
      return [];
    }

    return data || [];
  },

  async createCannedResponse(
    organizationId: string,
    createdBy: string,
    response: { shortCode: string; title: string; content: string; category?: string }
  ): Promise<CannedResponse | null> {
    const { data, error } = await supabase
      .from('canned_responses')
      .insert({
        organization_id: organizationId,
        short_code: response.shortCode,
        title: response.title,
        content: response.content,
        category: response.category || null,
        created_by: createdBy,
      })
      .select('*, creator:profiles!created_by(id, display_name)')
      .single();

    if (error) {
      console.error('Error creating canned response:', error);
      return null;
    }

    return data;
  },

  async updateCannedResponse(
    id: string,
    updates: Partial<{ short_code: string; title: string; content: string; category: string | null }>
  ): Promise<boolean> {
    const { error } = await supabase
      .from('canned_responses')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating canned response:', error);
      return false;
    }

    return true;
  },

  async deleteCannedResponse(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('canned_responses')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting canned response:', error);
      return false;
    }

    return true;
  },

  // Contacts
  async getContacts(
    organizationId: string,
    options?: { search?: string; role?: string }
  ): Promise<(Profile & { conversation_count?: number })[]> {
    let query = supabase
      .from('profiles')
      .select('*')
      .eq('organization_id', organizationId)
      .order('last_seen_at', { ascending: false, nullsFirst: false });

    if (options?.role) {
      query = query.eq('role', options.role);
    }

    if (options?.search) {
      const safe = sanitizeFilterValue(options.search);
      query = query.or(
        `display_name.ilike.%${safe}%,email.ilike.%${safe}%`
      );
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching contacts:', error);
      return [];
    }

    // Fetch conversation counts
    if (data && data.length > 0) {
      const userIds = data.map((c: Profile) => c.id);
      const { data: participantData } = await supabase
        .from('conversation_participants')
        .select('user_id, conversation_id')
        .in('user_id', userIds)
        .is('left_at', null);

      const countMap: Record<string, number> = {};
      (participantData || []).forEach((p: { user_id: string }) => {
        countMap[p.user_id] = (countMap[p.user_id] || 0) + 1;
      });

      return data.map((c: Profile) => ({
        ...c,
        conversation_count: countMap[c.id] || 0,
      }));
    }

    return data || [];
  },

  async getContact(contactId: string): Promise<(Profile & { metadata?: Record<string, unknown> }) | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', contactId)
      .single();

    if (error) {
      console.error('Error fetching contact:', error);
      return null;
    }

    return data;
  },

  async getContactConversations(contactId: string): Promise<Conversation[]> {
    const { data: participantData, error: pError } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', contactId)
      .is('left_at', null);

    if (pError || !participantData || participantData.length === 0) {
      return [];
    }

    const conversationIds = participantData.map((p: { conversation_id: string }) => p.conversation_id);

    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        agent:profiles!assigned_agent_id(id, display_name, avatar_url)
      `)
      .in('id', conversationIds)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching contact conversations:', error);
      return [];
    }

    return data || [];
  },

  // CSAT
  async submitCSAT(
    conversationId: string,
    rating: number,
    comment?: string
  ): Promise<boolean> {
    const updates: Record<string, unknown> = { csat_rating: rating };
    if (comment) updates.csat_comment = comment;

    const { error } = await supabase
      .from('conversations')
      .update(updates)
      .eq('id', conversationId);

    if (error) {
      console.error('Error submitting CSAT:', error);
      return false;
    }

    return true;
  },

  // Tags
  async getOrgTags(organizationId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('conversations')
      .select('tags')
      .eq('organization_id', organizationId)
      .not('tags', 'eq', '{}');

    if (error) {
      console.error('Error fetching org tags:', error);
      return [];
    }

    const allTags = (data || []).flatMap((c: { tags: string[] }) => c.tags || []);
    return [...new Set(allTags)].sort();
  },

  // Automation Rules
  async getAutomationRules(organizationId: string): Promise<AutomationRule[]> {
    const { data, error } = await supabase
      .from('automation_rules')
      .select('*')
      .eq('organization_id', organizationId)
      .order('execution_order', { ascending: true });

    if (error) {
      console.error('Error fetching automation rules:', error);
      return [];
    }

    return data || [];
  },

  async createAutomationRule(
    organizationId: string,
    createdBy: string,
    rule: Omit<AutomationRule, 'id' | 'organization_id' | 'created_by' | 'created_at' | 'updated_at'>
  ): Promise<AutomationRule | null> {
    const { data, error } = await supabase
      .from('automation_rules')
      .insert({
        organization_id: organizationId,
        created_by: createdBy,
        ...rule,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating automation rule:', error);
      return null;
    }

    return data;
  },

  async updateAutomationRule(id: string, updates: Partial<AutomationRule>): Promise<boolean> {
    const { error } = await supabase
      .from('automation_rules')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('Error updating automation rule:', error);
      return false;
    }

    return true;
  },

  async deleteAutomationRule(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('automation_rules')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting automation rule:', error);
      return false;
    }

    return true;
  },

  // Macros
  async getMacros(organizationId: string): Promise<Macro[]> {
    const { data, error } = await supabase
      .from('macros')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');

    if (error) {
      console.error('Error fetching macros:', error);
      return [];
    }

    return data || [];
  },

  async createMacro(
    organizationId: string,
    createdBy: string,
    macro: { name: string; description?: string; actions: MacroAction[]; visibility?: string }
  ): Promise<Macro | null> {
    const { data, error } = await supabase
      .from('macros')
      .insert({
        organization_id: organizationId,
        created_by: createdBy,
        name: macro.name,
        description: macro.description || null,
        actions: macro.actions,
        visibility: macro.visibility || 'organization',
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating macro:', error);
      return null;
    }

    return data;
  },

  async deleteMacro(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('macros')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting macro:', error);
      return false;
    }

    return true;
  },

  // SLA Policies
  async getSlaPolicies(organizationId: string): Promise<SlaPolicy[]> {
    const { data, error } = await supabase
      .from('sla_policies')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching SLA policies:', error);
      return [];
    }

    return data || [];
  },

  async createSlaPolicy(
    organizationId: string,
    policy: Omit<SlaPolicy, 'id' | 'organization_id' | 'created_at' | 'updated_at'>
  ): Promise<SlaPolicy | null> {
    const { data, error } = await supabase
      .from('sla_policies')
      .insert({
        organization_id: organizationId,
        ...policy,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating SLA policy:', error);
      return null;
    }

    return data;
  },

  async updateSlaPolicy(id: string, updates: Partial<SlaPolicy>): Promise<boolean> {
    const { error } = await supabase
      .from('sla_policies')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('Error updating SLA policy:', error);
      return false;
    }

    return true;
  },

  async deleteSlaPolicy(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('sla_policies')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting SLA policy:', error);
      return false;
    }

    return true;
  },

  // Webhooks
  async getWebhooks(organizationId: string): Promise<WebhookSubscription[]> {
    const { data, error } = await supabase
      .from('webhook_subscriptions')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching webhooks:', error);
      return [];
    }

    return data || [];
  },

  async createWebhook(
    organizationId: string,
    createdBy: string,
    webhook: { url: string; event_types: string[]; description?: string }
  ): Promise<WebhookSubscription | null> {
    const secret = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '');
    const { data, error } = await supabase
      .from('webhook_subscriptions')
      .insert({
        organization_id: organizationId,
        url: webhook.url,
        secret,
        event_types: webhook.event_types,
        description: webhook.description || null,
        created_by: createdBy,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating webhook:', error);
      return null;
    }

    return data;
  },

  async updateWebhook(id: string, updates: Partial<WebhookSubscription>): Promise<boolean> {
    const { error } = await supabase
      .from('webhook_subscriptions')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating webhook:', error);
      return false;
    }

    return true;
  },

  async deleteWebhook(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('webhook_subscriptions')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting webhook:', error);
      return false;
    }

    return true;
  },

  // Audit Log
  async getAuditLog(
    organizationId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<AuditLogEntry[]> {
    const limit = options?.limit || 50;
    let query = supabase
      .from('audit_log')
      .select('*, actor:profiles!actor_id(id, display_name, email)')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (options?.offset) {
      query = query.range(options.offset, options.offset + limit - 1);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching audit log:', error);
      return [];
    }

    return data || [];
  },

  // Agent Activity
  async getAgentActivity(
    organizationId: string,
    options?: { agentId?: string; limit?: number }
  ): Promise<AgentActivity[]> {
    let query = supabase
      .from('agent_activity_log')
      .select('*, agent:profiles!agent_id(id, display_name, email)')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .limit(options?.limit || 50);

    if (options?.agentId) {
      query = query.eq('agent_id', options.agentId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching agent activity:', error);
      return [];
    }

    return data || [];
  },

  // API Keys
  async getApiKeys(organizationId: string): Promise<ApiKey[]> {
    const { data, error } = await supabase
      .from('api_keys')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching API keys:', error);
      return [];
    }

    return data || [];
  },

  async createApiKey(
    organizationId: string,
    createdBy: string,
    name: string
  ): Promise<{ key: ApiKey; rawKey: string } | null> {
    // Generate a random API key
    const rawKey = `avk_${crypto.randomUUID().replace(/-/g, '')}`;
    const keyPrefix = rawKey.slice(0, 12);

    // Hash it for storage
    const encoder = new TextEncoder();
    const data = encoder.encode(rawKey);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = new Uint8Array(hashBuffer);
    const keyHash = Array.from(hashArray).map((b) => b.toString(16).padStart(2, '0')).join('');

    const { data: apiKey, error } = await supabase
      .from('api_keys')
      .insert({
        organization_id: organizationId,
        name,
        key_hash: keyHash,
        key_prefix: keyPrefix,
        created_by: createdBy,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating API key:', error);
      return null;
    }

    return { key: apiKey, rawKey };
  },

  async deleteApiKey(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('api_keys')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting API key:', error);
      return false;
    }

    return true;
  },

  async revokeApiKey(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('api_keys')
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
      console.error('Error revoking API key:', error);
      return false;
    }

    return true;
  },
};
